import { Injectable, inject, PLATFORM_ID, ApplicationRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  signInWithPhoneNumber,
  signOut,
  onAuthStateChanged,
  User,
  RecaptchaVerifier,
  ConfirmationResult,
  ApplicationVerifier,
  Auth,
  getAuth,
} from 'firebase/auth';
import { firebasePublicApp } from '../../firebase/firebase-public-init';
import { BehaviorSubject, Observable } from 'rxjs';
import { first } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly platformId = inject(PLATFORM_ID);
  private appRef = inject(ApplicationRef);
  private _authInstance: Auth | null = null;

  // BehaviorSubject — starts null, populated after 'load' event
  private readonly _user = new BehaviorSubject<User | null>(null);
  readonly user$: Observable<User | null> = this._user.asObservable();

  constructor() {
    // Defer Firebase Auth initialization until after page 'load' event.
    // This prevents the Auth persistence iframe from blocking LCP/FCP.
    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener(
        'load',
        () => {
          this._getAuth();
        },
        { once: true },
      );
    }
  }

  /**
   * Lazy getter — getAuth() is called only on first use, never at import time.
   * Attaches the auth-state listener on first use too, so callers that force
   * early init (e.g. route guards calling `.auth`) get `user$` populated
   * immediately instead of waiting for the deferred window 'load' listener.
   */
  private _getAuth(): Auth {
    if (!this._authInstance) {
      this._authInstance = getAuth(firebasePublicApp);
      if (isPlatformBrowser(this.platformId)) {
        onAuthStateChanged(this._authInstance, (u) => this._user.next(u));
      }
    }
    return this._authInstance;
  }

  /**
   * Immediately reflects an already-resolved user into `user$`, without
   * waiting for the async `onAuthStateChanged` callback to fire. Used by
   * route guards that have already awaited `authStateReady()`, to avoid a
   * render frame where `user$` is still null despite the user being signed in.
   */
  syncUser(user: User | null): void {
    this._user.next(user);
  }

  /** Public accessor for components that need the Auth instance (e.g. RecaptchaVerifier). */
  get auth(): Auth {
    return this._getAuth();
  }

  /**
   * Initializes the RecaptchaVerifier.
   * Must be called after the view is initialized and the element is present in the DOM.
   */
  createRecaptchaVerifier(elementId: string): ApplicationVerifier {
    const auth = this._getAuth();
    const isLocalhost =
      typeof window !== 'undefined' &&
      (location.hostname === 'localhost' || location.hostname === '127.0.0.1');

    const verifier = new RecaptchaVerifier(auth, elementId, {
      size: isLocalhost ? 'normal' : 'invisible',
      callback: () => {
        if (isLocalhost) {
          console.log('reCAPTCHA solved');
        }
      },
      'expired-callback': () => {
        if (isLocalhost) {
          console.warn('reCAPTCHA expired, please solve again.');
        }
      },
    });

    return verifier;
  }

  signInWithPhoneNumber(
    phoneNumber: string,
    appVerifier: ApplicationVerifier,
  ): Promise<ConfirmationResult> {
    return signInWithPhoneNumber(this._getAuth(), phoneNumber, appVerifier);
  }

  verifyOtp(
    confirmationResult: ConfirmationResult,
    verificationCode: string,
  ): Promise<User | null> {
    return confirmationResult
      .confirm(verificationCode)
      .then((result) => result.user)
      .catch((error) => {
        console.error('Error verifying code', error);
        throw error;
      });
  }

  logout(): Promise<void> {
    return signOut(this._getAuth());
  }

  getCurrentUser(): Promise<User | null> {
    return this.user$
      .pipe(first())
      .toPromise()
      .then((u) => u ?? null);
  }
}
